import { Settings, User, Lightbulb, ShieldCheck } from "lucide-react";
import { useTranslate, useUserMenu } from "ra-core";
import { Link, useLocation } from "react-router";
import { RefreshButton } from "@/components/admin/refresh-button";
import { ThemeToggle } from "@/components/theme/ThemeToggle";
import { UserMenu } from "@/components/admin/user-menu";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { Wizard_Entity_Onboarding } from "@/components/wizard/Wizard_Entity_Onboarding";
import { GlobalIdeaModal } from "@/components/entity/GlobalIdeaModal";
import {
  getAdminMode,
  isAdministrator,
  setAdminMode,
  useAdminAccess,
} from "@/providers/firebase/authProvider";
import { EntitySwitcher } from "./EntitySwitcher";
import { TasteRunItem } from "./TasteRunItem";

export const TopBar = () => {
  const location = useLocation();
  const [openWizard, setOpenWizard] = useState(false);
  const [wizardMinimizedStep, setWizardMinimizedStep] = useState<number | null>(null);
  const [openIdea, setOpenIdea] = useState(false);
  const showAdmin = useAdminAccess();
  const admin = isAdministrator();

  const handleMinimize = (step: number) => {
    setOpenWizard(false);
    setWizardMinimizedStep(step);
  };

  const handleReopen = () => {
    setOpenWizard(true);
    setWizardMinimizedStep(null);
  };

  const handleClose = () => {
    setOpenWizard(false);
    setWizardMinimizedStep(null);
  };

  return (
    <>
      <header className="flex h-12 shrink-0 items-center gap-2 px-4 border-b border-border">
        {/* T103 / D-009: company rail (pills + real red outstanding badges) when >1 company */}
        <EntitySwitcher />

        {/*
         * P216 T0300: the run a founder started on the public doorway, carried into the shell.
         * It sits beside the company rail rather than in the right-hand cluster because it is about
         * HIS run, not one more tool — and it renders nothing at all when there is no run to carry.
         * The vertical shell mounts it separately (see the component's own note).
         */}
        <TasteRunItem />
        <div className="flex-1" />

        {/* Minimized wizard indicator */}
        {wizardMinimizedStep !== null && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleReopen}
            className="flex items-center gap-2 border-cyan-500 text-cyan-600 dark:text-cyan-400 animate-pulse"
          >
            <span className="h-2 w-2 rounded-full bg-cyan-500" />
            Setup in progress · Step {wizardMinimizedStep} of 7
          </Button>
        )}

        {/* Global "I have an idea" CTA — admin only until launch-ready */}
        {showAdmin && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOpenIdea(true)}
            className="flex items-center gap-1.5 border-primary/40 text-primary hover:bg-primary/8 hover:text-primary"
          >
            <Lightbulb className="h-4 w-4" />
            <span className="hidden sm:inline">I have an idea</span>
          </Button>
        )}

        {/* Admin / user mode switch (admins only) */}
        {admin && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAdminMode(!getAdminMode())}
            title={showAdmin ? "You see admin items. Click to preview user mode." : "You see what a regular user sees. Click to switch to admin mode."}
            className="flex items-center gap-1.5"
          >
            <ShieldCheck className="h-4 w-4" />
            <span className="hidden sm:inline">{showAdmin ? "Admin mode" : "User mode"}</span>
          </Button>
        )}

        {/* Right side: Theme toggle, Refresh, User menu */}
        <ThemeToggle />
        {/* ONE REFRESH CONTROL PER SURFACE (P080 D-125, PO 2026-09-21: *"we have two refresh
            buttons… Let's keep it clean and simple."*). The Messages surface owns a working,
            word-bearing `Refresh` bound to its own reload; THIS global control is react-admin's
            `useRefresh()`, which cannot refresh that surface at all (it loads through plain API
            calls — `EmailPage.tsx`). Two controls meant one did nothing, so the dead one is
            suppressed here, on this surface only. Reverse when T1337 puts it on the query cache.
            ⚠️ AND THIS IS THE COMPONENT THAT RENDERS. The first two attempts at this fix were made in
            `Header.tsx`, which NOTHING imports — `Layout.tsx` renders `CrmSidebar` + `TopBar` — so
            they changed no pixel while reporting success. A browser probe caught it; reading code
            did not. */}
        {!location.pathname.startsWith("/messages") && <RefreshButton />}
        {/* P206 T0200 (D-206-005) — this menu obeys the ADMIN MODE switch, the SAME control the
            sidebar already obeyed. It used `CanAccess`, which resolves the role from
            `user.administrator` — the IDENTITY — so a user-mode screen still offered admin rows.
            The PO found exactly this: "i am in user mode and not in admin mode. so that means you
            are not respecting the button on top right". Identity governs what a person MAY reach;
            the switch governs what SHOWS. One switch, every surface. */}
        <UserMenu>
          <ProfileMenu />
          {/* P206 T0202 — EVERY person has settings now, so this row is not admin-gated: it opens
              the ten settings that decide how Raisy behaves for HIM (D-206-001). The admin surface
              keeps its own row below, under a name of its own — D-206-002 named the pair
              `Settings` (the person) and `Admin Settings` (the admin), because two menu rows
              labelled `Settings` pointing at different pages is exactly the ambiguity that makes a
              person distrust what he is looking at. */}
          <SettingsMenu />
          {showAdmin && (
            <>
              {/* ⚠️ THE `Users` ROW IS GONE (P206 D-206-013, PO 2026-09-22: *"MOCKUP, for sure remove!"*).
                  It opened `/sales`, the inherited demo CRM's list of five generated people
                  (`janedoe@atomic.dev` + four) — mockup data reachable in a product that has real
                  people, which is what the PO asked about in IN-05: *"i see weird users, who are
                  those ? mockup ? why would i see them there ?"*. Admin mode gated the ROW; it did
                  not remove the thing, and the `<Resource name="sales">` registration below still
                  answered a typed `/sales`. Both halves are retired — the files stay on disk, the
                  precedent P013 D-006 set for `/import`. */}
              <AdminSettingsMenu />
            </>
          )}
        </UserMenu>
      </header>
      <Wizard_Entity_Onboarding
        open={openWizard}
        onClose={handleClose}
        onMinimize={handleMinimize}
        onEntityCreated={() => { setWizardMinimizedStep(null); }}
      />
      <GlobalIdeaModal open={openIdea} onOpenChange={setOpenIdea} />
    </>
  );
};

const ProfileMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) throw new Error("<ProfileMenu> must be used inside <UserMenu>");
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/profile" className="flex items-center gap-2">
        <User />
        {translate("crm.profile.title")}
      </Link>
    </DropdownMenuItem>
  );
};

/** The person's own settings — available to everyone, admin mode or not (P206 T0202). */
const SettingsMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) throw new Error("<SettingsMenu> must be used inside <UserMenu>");
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/settings" className="flex items-center gap-2">
        <Settings />
        {translate("crm.settings.title")}
      </Link>
    </DropdownMenuItem>
  );
};

/**
 * The ADMIN settings — the workspace taxonomy, rendered only in admin mode (T0200's gate).
 *
 * The label is a literal and not a translation key on purpose: `Admin Settings` is the name
 * D-206-002 gave this page, and there is no key for it yet. Renaming the PAGE itself (its h1, its
 * title) is T0204's work — the menu may not wait for it, because two rows both reading `Settings`
 * is the confusion this pairing exists to remove.
 */
const AdminSettingsMenu = () => {
  const userMenuContext = useUserMenu();
  if (!userMenuContext) throw new Error("<AdminSettingsMenu> must be used inside <UserMenu>");
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/admin/settings" className="flex items-center gap-2">
        <ShieldCheck />
        Admin Settings
      </Link>
    </DropdownMenuItem>
  );
};
